import { getDomainName, langFallback$Fetch } from '~/lang/utils/langfetch'
import { getTransformObject } from '~/common/photos/transforms'
import { getGeoPointFromWKTPoint } from '~/utility/geo/GeoLocation'
import { safeJSONParse } from '../../../utility/json'
import { logger } from '../../../utility/logger'
import {
  FavouriteCampsiteDetails,
  FavouriteCampsitesRequestData,
  FavouritesBasicWithWishlistsAndPrimaryPhoto,
  Wishlist,
  WishlistFullData,
} from '../types'

type PrivacyType = 'private' | 'public'
type CampsitesRequestData = {
  campsite_id: number
  notes?: string
}

interface WishlistWithItemsFetch
  extends Omit<Wishlist, 'isOwner' | 'itemCount' | 'primaryPhoto'> {
  is_owner: boolean
  is_main?: boolean
  item_count: number
  primary_photo: { title: string; url: string } | null
  items: Record<
    number,
    {
      campsite_id: string
      created: string
      notes: string
    }
  >
}
type WishlistWithItems = Wishlist & {
  items: Record<
    number,
    {
      created: string
      notes: string
    }
  >
}

interface PhotoResponse {
  caption?: string
  url: {
    master_image: string
  }
}
type EldarionFetchResult = {
  alfred_with_lists?: {
    favourite_dated_with_lists?: string
  }
  favourites_primary_photo: string
}

export class FavouritesRepository {
  constructor(
    private langCode: string,
    private fetchOptions?,
  ) {}

  async add(campsiteId: string) {
    const url = `/_/togglefavourite/${campsiteId}/?domain=${getDomainName()}`
    const response = await this.doEldarionFetch<{ primary_photo: string }>(
      url,
      'POST',
    )
    return safeJSONParse(response.primary_photo, null)
  }

  async addNewList(
    name: string,
    campsites?: CampsitesRequestData[],
    privacy: PrivacyType = 'private',
  ) {
    const body = {
      name,
      status: privacy,
      campsites,
    }
    try {
      const response: {
        list_id: string
      } = await this.doFetch('/_/favourite-list/', 'POST', { body })
      return { listId: response.list_id }
    } catch (error) {
      const { data } = error as { data: { errors: Record<string, string[]> } }
      throw data.errors
    }
  }

  async changeListPrivacy(listId: string, privacy: PrivacyType) {
    const body = { status: privacy }
    const response = await this.doFetch(
      `/_/favourite-list/${listId}/`,
      'PATCH',
      { body },
    )
    return response
  }

  async addFavouriteToWishlist(
    listId: string,
    campsiteId: string,
    note: string,
  ) {
    const body = { campsite: campsiteId, notes: note }
    const { list_id, item_count, primary_photo, ...rest } = await this.doFetch<{
      list_id: string
      item_count: number
      primary_photo: { title: string; url: string } | null
    }>(`/_/favourite-list/${listId}/add-campsite/`, 'POST', { body })
    return {
      id: list_id,
      itemCount: item_count,
      primaryPhoto: primary_photo,
      ...rest,
    }
  }

  async remove(campsiteId: string) {
    const url = `/_/togglefavourite/${campsiteId}/?domain=${getDomainName()}`
    const response = await this.doEldarionFetch<{ primary_photo: string }>(
      url,
      'POST',
    )
    return safeJSONParse(response.primary_photo, null)
  }

  async removeFromWishlist(listId: string, campsiteId: string) {
    await this.doFetch(`/_/favourite-list/${listId}/${campsiteId}/`, 'DELETE')
  }

  async getAllWishlists(): Promise<Record<string, WishlistFullData> | false> {
    try {
      const wishlists = await this.doFetch<
        Record<string, WishlistWithItemsFetch>
      >('/_/favourite-list/', 'GET')

      // Check if response is valid (not undefined from 401 handling)
      if (!wishlists || typeof wishlists !== 'object') {
        return false
      }

      const transformedWishlists: Record<string, WishlistFullData> = {}
      for (const [
        key,
        { is_owner, is_main, item_count, primary_photo, ...rest },
      ] of Object.entries(wishlists)) {
        transformedWishlists[key] = {
          ...rest,
          isOwner: is_owner,
          isMain: is_main,
          itemCount: item_count,
          primaryPhoto: primary_photo,
        }
      }
      return transformedWishlists
    } catch (error: any) {
      // Check for 401 in various error formats (fetch, axios, etc.)
      if (
        error?.status === 401 ||
        error?.response?.status === 401 ||
        error?.statusCode === 401 ||
        error?.data?.status === 401 ||
        (error?.message && error.message.includes('401'))
      ) {
        // User not logged in - this is expected behavior, not an error
        return false
      } else {
        throw error
      }
    }
  }

  async removeWishlist(wishlistId: string) {
    // TODO: add status check
    return await this.doFetch(`/_/favourite-list/${wishlistId}/`, 'DELETE')
  }

  async getWishlistDetails(wishlistId: string): Promise<WishlistWithItems> {
    const { is_owner, item_count, primary_photo, ...rest } =
      await this.doFetch<WishlistWithItemsFetch>(
        `/_/favourite-list/${wishlistId}/`,
        'GET',
      )
    return {
      ...rest,
      isOwner: is_owner,
      itemCount: item_count,
      primaryPhoto: primary_photo,
    }
  }

  async getDetailedCampsitesNoPagination(
    wishlistId: string,
  ): Promise<WishlistFullData> {
    const response = await this.doFetch<any>(
      `/_/favourite-campsite-details/no-pagination/${wishlistId}/`,
    )
    logger('FavouritesRepository.getDetailedCampsitesNoPagination', response)
    const campsites = this.mapResults(response.results)
    delete response.results
    return {
      ...response,
      campsites,
      id: response.id || wishlistId,
      itemCount: campsites.length,
    }
  }

  async updateFavouriteInWishlist(
    listId: string,
    campsiteId: string,
    note: string,
  ) {
    try {
      const body = { list_id: listId, campsite_id: campsiteId, notes: note }
      const response: {
        campsite_id: string
        list_id: string
        notes: string
      } = await this.doFetch('/_/favourite-list/notes/', 'PUT', { body })
      return {
        data: {
          campsiteId: response.campsite_id,
          listId: response.list_id,
          notes: response.notes,
        },
      }
    } catch (error) {
      return {
        data: error,
      }
    }
  }

  async getAll(): Promise<FavouritesBasicWithWishlistsAndPrimaryPhoto> {
    try {
      const response = await this.doFetch('/_/favourite-list/all/')
      // Check if response is valid (not undefined from 401 handling)
      if (!response || typeof response !== 'object') {
        return {
          favouriteDatedWithLists: {},
          favouritesPrimaryPhoto: {},
        }
      }
      return {
        favouriteDatedWithLists: response.favourites || {},
        favouritesPrimaryPhoto: response.favourites_primary_photo || {},
      }
    } catch (error: any) {
      // Check for 401 in various error formats
      if (
        error?.status === 401 ||
        error?.response?.status === 401 ||
        error?.statusCode === 401 ||
        error?.data?.status === 401 ||
        (error?.message && error.message.includes('401'))
      ) {
        // User not logged in - return empty data
        return {
          favouriteDatedWithLists: {},
          favouritesPrimaryPhoto: {},
        }
      }
      throw error
    }
  }

  async getDetailedCampsites(
    page?: number,
    listId?: string,
  ): Promise<FavouriteCampsitesRequestData> {
    const query: string[] = []
    if (page) {
      query.push(`page=${page}`)
    }
    if (listId) {
      query.push(`list_id=${listId}`)
    }
    const queryPart = query.length ? `?${query.join('&')}` : ''
    const response: any = await this.doFetch(
      `/_/favourite-campsite-details/${queryPart}`,
    )
    return {
      ...response,
      results: this.mapResults(response.results),
    }
  }

  mapResults(results): FavouriteCampsiteDetails[] {
    return results.map((campsite: any) => {
      return {
        ...campsite,
        id: campsite.id.toString(),
        status: campsite.bookable ? 'bookable' : 'free',
        nutshells: campsite.nutshells || [],
        point: campsite.point
          ? getGeoPointFromWKTPoint(campsite.point)
          : undefined,
        primaryPhoto: campsite.primaryPhoto
          ? this.mapPhoto(campsite.primaryPhoto)
          : undefined,
        leadPriceAdults:
          campsite.leadPricePersonsIncluded ||
          Number(process.env.defaultAdultsCount),
      }
    })
  }

  private mapPhoto(photo: PhotoResponse) {
    return {
      caption: photo.caption || '',
      url: getTransformObject('favourite', photo.url.master_image),
    }
  }

  private async doFetch<T>(
    url: string,
    method: string = 'GET',
    content: object = {},
  ) {
    return await langFallback$Fetch<T>(this.langCode, url, {
      method,
      ...content,
      ...this.fetchOptions,
      onResponseError({ response }) {
        // Silently handle 401 errors - they're expected when user isn't logged in
        if (response?.status === 401) {
          // Don't log to console, just let the error bubble up to be handled
          return
        }
      },
    })
  }

  private async doEldarionFetch<T>(url: string, method: string = 'GET') {
    const headers: Record<string, string> = {
      'X-ELDARION-AJAX': 'true',
      'X-Requested-With': 'XMLHttpRequest',
      ...(this.fetchOptions?.headers || {}),
    }
    return await langFallback$Fetch<T>(this.langCode, url, {
      method,
      ...this.fetchOptions,
      headers,
      onResponseError({ response }) {
        // Silently handle 401 errors - they're expected when user isn't logged in
        if (response?.status === 401) {
          // Don't log to console, just let the error bubble up to be handled
          return
        }
      },
    })
  }

  async updateWishlist(
    listId: string,
    updates: { name: string; status?: PrivacyType },
  ) {
    try {
      const response = await this.doFetch(
        `/_/favourite-list/${listId}/`,
        'PATCH',
        { body: updates },
      )
      return response
    } catch (error) {
      const { data } = error as { data: { errors: Record<string, string[]> } }
      throw data.errors
    }
  }
}
